import { AppText } from "@/components/AppText";
import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import Icons from "@/assets/icons";
import { router } from "expo-router";
import { showSuccess, showError } from "@/utils/toast";
import AuthServices from "@/services/authServices";
import { storageUtils } from "@/utils/storage";
import { clearGlobalSessionId } from "@/services";
import React, { useState } from "react";
import {
  ActivityIndicator,
  Platform,
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { APP_BASE_URL } from "@/constants/Configs";

const DeleteAccountScreen = () => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleKeepAccount = () => {
    router.back();
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);

      const response = await AuthServices.deleteAccount();

      if (response.success) {
        // Clear session data
        storageUtils.removeSessionId();
        clearGlobalSessionId();

        showSuccess("Account deleted successfully", "Goodbye!");

        // Navigate to signin screen
        setTimeout(() => {
          if (Platform.OS === "web") {
            window.location.href = `${APP_BASE_URL}/logout`;
          } else {
            router.replace("/welcome");
          }
        }, 100);
      } else {
        showError(
          response.message || "Failed to delete account",
          "Delete Failed"
        );
      }
    } catch (error) {
      console.error("Delete account error:", error);
      showError(
        "An error occurred while deleting your account",
        "Delete Failed"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header title="" showBackButton={true} />

      <View style={styles.content}>
        {/* Sad Face Icon */}
        <View style={styles.iconContainer}>
          <Icons.Sad />
        </View>

        {/* Title */}
        <Text style={styles.title}>Delete your account?</Text>

        {/* Description */}
        <Text style={styles.description}>
          Your account will be deleted and you will lose access to class
          actions, refund opportunities, and unclaimed money from Sparrow.
        </Text>
      </View>

      {/* Bottom Actions */}
      <View style={styles.bottomSection}>
        <ActionButton title="Keep my account" onPress={handleKeepAccount} />

        <TouchableOpacity
          onPress={handleDeleteAccount}
          style={[
            styles.deleteAnywayButton,
            isDeleting && styles.deleteAnywayButtonDisabled,
          ]}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color="#6B7280" />
              <AppText style={[styles.deleteAnywayText, { marginLeft: 8 }]}>
                Deleting...
              </AppText>
            </View>
          ) : (
            <AppText style={styles.deleteAnywayText}>Delete Anyway</AppText>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

export default DeleteAccountScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  iconContainer: {
    marginBottom: 32,
  },
  title: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 24,
    marginBottom: 16,
  },
  description: {
    fontFamily: FontFamilies.InterTight.Regular,
    fontSize: 15,
    lineHeight: 24,
    color: "#3E464D",
  },
  bottomSection: {
    paddingHorizontal: 24,
    paddingBottom: 34,
    gap: 16,
  },
  deleteAnywayButton: {
    alignItems: "center",
  },
  deleteAnywayButtonDisabled: {
    opacity: 0.6,
  },
  deleteAnywayText: {
    fontSize: 13,
    fontFamily: FontFamilies.InterTight.Medium,
    color: "#00000050",
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
});
